import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/ja";
import "dayjs/locale/en";
import type { EventFormData } from "@/features/study-group-events/type";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import i18n from "@/lib/i18n";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

interface FormatOptions {
  year?:
    | "auto" // 今年なら年省略
    | "always" // 常に年を表示
    | "never"; // 年を表示しない
  locale?: string;
  displayTimezone?: "local" | "event";
}

export function formatEventReview(event: EventFormData): string {
  // 終日イベント
  if (event.isAllDay) {
    // 同日の場合は日付のみを表示
    if (event.startDate === event.endDate) return event.startDate;

    // 複数日の場合は日付を表示
    return `${event.startDate} - ${event.endDate}`;
  }

  // 時刻指定イベント
  // 同日の場合は時刻のみを表示
  const base = `${event.startDate} ${event.startTime}`;
  if (event.startTime === event.endTime) return base;

  // 複数日の場合は日付と時刻を表示
  return `${base} - ${event.endDate} ${event.endTime}`;
}

/**
 * イベント一覧表示用の日時フォーマット文字列を生成
 *
 * @param event EventApiResponse
 * @param options フォーマットオプション
 * @returns フォーマットされた日時文字列
 */
export function formatEventListRow(
  event: EventApiResponse,
  {
    year = "auto",
    locale = i18n.language,
    displayTimezone = "local",
  }: FormatOptions = {},
): string {
  try {
    const {
      isAllDay,
      startZoned,
      endZoned,
      sameDay,
      showStartYear,
      showEndYear,
      tzNote,
      tz,
    } = buildEventDateContext(event, locale, year, displayTimezone);

    // 終日イベント（イベントTZ表示）
    if (isAllDay) {
      const startLabel = formatDateLabel(startZoned, showStartYear, locale, tz);
      if (sameDay) {
        return tzNote ? `${startLabel} ${tzNote}` : startLabel;
      }

      const endLabel = formatDateLabel(endZoned, showEndYear, locale, tz);
      return tzNote
        ? `${startLabel} - ${endLabel} ${tzNote}`
        : `${startLabel} - ${endLabel}`;
    }

    // 時間指定イベント
    const startDisplay =
      displayTimezone === "event" ? startZoned : startZoned.local();
    const endDisplay =
      displayTimezone === "event" ? endZoned : endZoned.local();

    const startLabel = formatDateLabel(
      startDisplay,
      showStartYear,
      locale,
      displayTimezone === "event" ? tz : undefined,
    );
    const startTime = startDisplay.locale(locale).format("LT");
    const endTime = endDisplay.locale(locale).format("LT");
    if (sameDay) {
      return `${startLabel} ${startTime} - ${endTime}`;
    }

    const endLabel = formatDateLabel(
      endDisplay,
      showEndYear,
      locale,
      displayTimezone === "event" ? tz : undefined,
    );
    return `${startLabel} ${startTime} - ${endLabel} ${endTime}`;
  } catch (error) {
    console.warn("Error formatting event list row:", error);
    return event.start_datetime;
  }
}

/**
 * 年を表示するかどうかを判定（単一日時に対して）
 * @param d 対象日時（表示基準TZに合わせた Dayjs）
 * @param mode 年表示モード
 * @param todayYear 表示基準TZにおける「今年」の年
 */
function shouldShowYearFor(
  d: dayjs.Dayjs,
  mode: "auto" | "always" | "never",
  todayYear: number,
): boolean {
  if (mode === "always") return true;
  if (mode === "never") return false;

  return d.year() !== todayYear;
}

/**
 * 日付ラベルをフォーマット
 *
 * @param d 日時
 * @param showYear 年を表示するか
 * @param locale ロケール
 * @param timeZone 表示に使用するタイムゾーン
 * @returns フォーマットされた日付文字列
 */
function formatDateLabel(
  d: dayjs.Dayjs,
  showYear: boolean,
  locale: string,
  timeZone?: string,
): string {
  if (showYear) {
    // 年あり: "ll (ddd)" 形式
    return d.locale(locale).format("ll (ddd)");
  } else {
    // 年なし: Intl.DateTimeFormatを使用
    const date = d.toDate();
    const formatter = new Intl.DateTimeFormat(locale, {
      month: locale.startsWith("ja") ? "long" : "short",
      day: "numeric",
      weekday: "short",
      ...(timeZone ? { timeZone } : {}),
    });
    return formatter.format(date);
  }
}

/**
 * 終日イベント用のタイムゾーン注記を生成（ユーザーTZとイベントTZが異なる場合）
 * 例: ja → （日本標準時） / en → (GMT+9) 等
 */
function getAllDayTzNote(eventTz: string, locale: string): string {
  try {
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!eventTz || !userTz || eventTz === userTz) return "";

    const now = new Date();
    const partsShort = new Intl.DateTimeFormat(locale, {
      timeZone: eventTz,
      timeZoneName: "short",
    }).formatToParts(now);
    const shortName = partsShort.find((p) => p.type === "timeZoneName")?.value;

    const label = shortName || eventTz;
    return `(${label})`;
  } catch (_e) {
    return "";
  }
}

// イベントの状態に合わせた日時コンテキストを作成
function buildEventDateContext(
  event: EventApiResponse,
  locale: string,
  year: "auto" | "always" | "never",
  displayTimezone: "local" | "event",
) {
  const tz = event.time_zone;
  const isAllDay = event.all_day === 1;

  // Humhub のレスポンスで終日イベントは end が翌日同時刻で返ってくるので、1日引いた end を用いるようにする
  // 例: 日本時間2025年1月1日の終日:
  //     start_datetime: 2025-01-01 09:00:00
  //     end_datetime: 2025-01-02 09:00:00
  const endZonedRaw = dayjs.tz(event.end_datetime, tz);
  const endZoned = isAllDay ? endZonedRaw.subtract(1, "day") : endZonedRaw;
  const startZoned = dayjs.tz(event.start_datetime, tz);

  const tzNote = isAllDay ? getAllDayTzNote(tz, locale) : "";
  const sameDay = startZoned.isSame(endZoned, "day");

  const todayYear =
    isAllDay || displayTimezone === "event"
      ? dayjs().tz(tz).year()
      : dayjs().year();
  const startForYearCheck =
    displayTimezone === "event" ? startZoned : startZoned.local();
  const endForYearCheck =
    displayTimezone === "event" ? endZoned : endZoned.local();
  const showStartYear = shouldShowYearFor(startForYearCheck, year, todayYear);
  const showEndYear = shouldShowYearFor(endForYearCheck, year, todayYear);

  return {
    tz,
    isAllDay,
    startZoned,
    endZoned,
    sameDay,
    tzNote,
    showStartYear,
    showEndYear,
  };
}

// 未来実装の雛形（コメントのみ）
/*
export interface FormatEventDetailOptions {
  year?: "auto" | "always" | "never";
  locale?: string;
}

export interface EventDateParts {
  date: string; // 例: '1/14' (ja), 'Jan 14' (en)
  weekday: string; // 例: '日' (ja), 'Sun' (en)
  time: string; // 例: '16:00' (時間指定時), '' (終日)
}

// TODO: 詳細表示用のフォーマット実装
// - formatEventDetail: 複数行表示に対応し、より詳細な日時情報を提供
// - getEventDateParts: カード左メタ等で使用する個別パーツを提供
*/
